/**
 * HR Candidates Tab Component
 * Displays candidates in onboarding pipeline with filters
 */

import React, { useState, useMemo } from 'react';
import { Search, Eye } from 'lucide-react';
import { ONBOARDING_STATUSES, AGING_BUCKETS, STATUS_COLORS, STATUS_LABELS } from '../../constants/hrConstants';

const HRCandidatesTab = ({
  acceptedCandidates = [],
  clients = [],
  recruiters = [],
  onCandidateSelect
}) => {
  const [filters, setFilters] = useState({
    client: 'all',
    recruiter: 'all',
    onboardingStatus: 'all',
    agingBucket: 'all',
    searchTerm: ''
  });

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
  };

  // Get status label
  const getStatusLabel = (status) => {
    return STATUS_LABELS[status] || status;
  };

  // Filter candidates
  const filteredCandidates = useMemo(() => {
    return acceptedCandidates.filter(cand => {
      if (filters.client !== 'all' && cand.client_name !== filters.client) return false;
      if (filters.recruiter !== 'all' && cand.recruiter_id !== filters.recruiter) return false;
      if (filters.onboardingStatus !== 'all' && cand.onboardingStatus !== filters.onboardingStatus) return false;
      if (filters.agingBucket !== 'all' && cand.agingBucket.key !== filters.agingBucket) return false;
      if (filters.searchTerm) {
        const search = filters.searchTerm.toLowerCase();
        return cand.candidate_name?.toLowerCase().includes(search) ||
               cand.job_title?.toLowerCase().includes(search) ||
               cand.client_name?.toLowerCase().includes(search);
      }
      return true;
    });
  }, [acceptedCandidates, filters]);

  return (
    <div className="space-y-4" data-testid="hr-candidates-tab">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="candidates-search-input"
            />
          </div>
          <select
            value={filters.client}
            onChange={(e) => setFilters({...filters, client: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="candidates-client-filter"
          >
            <option value="all">All Clients</option>
            {clients.map(c => (
              <option key={c.id} value={c.company_name || c.client_name}>
                {c.company_name || c.client_name}
              </option>
            ))}
          </select>
          <select
            value={filters.recruiter}
            onChange={(e) => setFilters({...filters, recruiter: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="candidates-recruiter-filter"
          >
            <option value="all">All Recruiters</option>
            {recruiters.map(r => (
              <option key={r.id} value={r.id}>{r.full_name || r.username}</option>
            ))}
          </select>
          <select
            value={filters.onboardingStatus}
            onChange={(e) => setFilters({...filters, onboardingStatus: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="candidates-status-filter"
          >
            <option value="all">All Statuses</option>
            {Object.values(ONBOARDING_STATUSES).map(status => (
              <option key={status} value={status}>{getStatusLabel(status)}</option>
            ))}
          </select>
          <select
            value={filters.agingBucket}
            onChange={(e) => setFilters({...filters, agingBucket: e.target.value})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="candidates-aging-filter"
          >
            <option value="all">All Aging</option>
            {AGING_BUCKETS.map(bucket => (
              <option key={bucket.key} value={bucket.key}>{bucket.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Candidates Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full" data-testid="candidates-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recruiter</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Req</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Offer Accepted</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Pending</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Onboarding Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredCandidates.map(cand => (
              <tr key={cand.id} className="hover:bg-gray-50" data-testid="candidate-row">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {cand.candidate_name?.charAt(0) || 'C'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{cand.candidate_name}</p>
                      <p className="text-xs text-gray-500">{cand.candidate_email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{cand.client_name}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{cand.recruiter_name}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{cand.job_title}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{formatDate(cand.offerAcceptedDate)}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${cand.agingBucket.color}`}>
                    {cand.daysSinceAccepted} days
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(cand.onboardingStatus)}`}>
                    {getStatusLabel(cand.onboardingStatus)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => onCandidateSelect?.(cand)}
                    className="text-blue-600 hover:text-blue-700"
                    data-testid="view-candidate-btn"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {filteredCandidates.length === 0 && (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                  No candidates found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HRCandidatesTab;
